import { useEffect, useRef } from 'react';
import { BACKEND_URL } from './api';
import { getSessionToken } from './authStorage';

/**
 * Subscribe to server-sent real-time events for the logged-in tenant.
 * `onEvent` receives objects shaped like { event: 'orders'|'kds'|'tables'|'payments', data: {} }.
 * The connection auto-reconnects; polling in each page remains as a safety fallback.
 */
export function useRealtime(onEvent) {
  const cb = useRef(onEvent);
  cb.current = onEvent;

  useEffect(() => {
    const token = getSessionToken();
    if (!token || !BACKEND_URL || typeof EventSource === 'undefined') return undefined;

    let es = null;
    let stopped = false;
    let retryTimer = null;

    const connect = () => {
      if (stopped) return;
      try {
        es = new EventSource(`${BACKEND_URL}/api/events?token=${encodeURIComponent(token)}`);
      } catch (e) {
        return;
      }
      es.onmessage = (e) => {
        if (!e || !e.data) return;
        try {
          const msg = JSON.parse(e.data);
          cb.current && cb.current(msg);
        } catch (_) { /* ignore non-JSON keepalives */ }
      };
      es.onerror = () => {
        // readyState 2 = CLOSED. Reconnect manually after a short delay.
        if (es && es.readyState === 2 && !stopped) {
          es.close();
          retryTimer = setTimeout(connect, 3000);
        }
      };
    };

    connect();

    return () => {
      stopped = true;
      if (retryTimer) clearTimeout(retryTimer);
      if (es) es.close();
    };
  }, []);
}
